import { App } from '@capacitor/app';
import { DeviceSyncField } from '@am3nd-org/pointcraft-types';
import { Network, type ConnectionStatus } from '@capacitor/network';

import { trackedApiFetch } from 'src/utils/http/tracked-fetch';
import { getHealthRequestHeaders } from 'src/utils/http/health-request-metadata';
import { syncDeviceSyncV4Fields } from 'src/utils/Services/device-sync-v4.service';
import { recoverMissingApplicationContext } from 'src/utils/Services/application-context-recovery.service';
import {
  maybeRepairDeviceSession,
  resetDeviceSessionRepairState,
  type DeviceSessionRepairOutcome,
  getDeviceSessionRepairRetryDelayMs,
} from 'src/utils/Services/auth-v3/session-repair';

import { useSystemStore } from 'src/store/SystemStore';
import { SITE_ID, SiteIdEnum } from 'src/config-global';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { drainTerminalAssignmentsBeforeOnline } from 'src/features/offline-mutations/offline-mutation-outbox';
import useAuthStore, {
  recoverAuthSession,
  retryOfflineIdentityVerification,
} from 'src/auth/store/firebase/auth-store';

import { abortableProbe } from './abortable-probe';
import { evaluateOfflineEligibility } from './offline-eligibility';
import { isFirstPartyApiOutageSimulated } from './connectivity-debug-overrides';
import { FIRST_PARTY_TRANSPORT_FAILURE_EVENT } from './first-party-transport-signal';
import { useConnectivityStore, EMPTY_OFFLINE_ELIGIBILITY } from './connectivity-store';
import { INTERNET_ACCESS_CHECK_URL, requestInternetAccessProbe } from './internet-access-probe';
import {
  getCurrentOfflineShellAvailable,
  getCurrentOfflineShellBlockReason,
} from './offline-shell-access';
import {
  beginConnectivityProbe,
  type ConnectivityHealthPhase,
  reportConnectivityHealthDecision,
} from './connectivity-probe-events';
import type {
  ConnectivityIssue,
  OfflineEligibility,
  StartupRecoveryTask,
  OfflineEligibilityReason,
  ConnectivityConnectionType,
} from './types';

// While recovery is pending, the staged recheck (device → internet →
// PointCraft) reruns once per minute; only device disconnection skips PointCraft.
export const CONNECTIVITY_RETRY_INTERVAL_MS = 60_000;
export const CONNECTIVITY_INTERNET_CHECK_INTERVAL_MS = 5_000;
export const CONNECTIVITY_ONLINE_CHECK_INTERVAL_MS = 30_000;
// Verified offline mode rechecks faster than pending recovery: its published
// reachability is what arms the automatic back-online run (30s stability), so
// a restored link is noticed within ~30s instead of up to a minute.
export const CONNECTIVITY_OFFLINE_MODE_RECHECK_INTERVAL_MS = 30_000;
export const CONNECTIVITY_ISSUE_GRACE_MS = 2_000;
// First-party health checks are deliberately shorter than normal API calls,
// but must still tolerate merchant networks with elevated latency. A single
// deadline failure is only a candidate outage; run a quiet confirmation after
// the grace window before publishing unavailability.
export const CONNECTIVITY_FIRST_PARTY_REQUEST_TIMEOUT_MS = 15_000;
export const CONNECTIVITY_CONFIRMATION_TIMEOUT_MS = 30_000;
// A transient eligibility failure (secure storage or the local DB briefly
// unreadable) re-evaluates on this short bounded backoff instead of leaving
// Cash/Card latched off until the next full recovery interval.
export const CONNECTIVITY_TRANSIENT_ELIGIBILITY_RETRY_MS = 5_000;
export const CONNECTIVITY_TRANSIENT_ELIGIBILITY_MAX_ATTEMPTS = 3;
export const CONNECTIVITY_SESSION_REPAIR_MAX_RETRIES = 3;
const INTERNET_REQUEST_TIMEOUT_MS = 3_000;
const REQUIRED_RECOVERY_FIELDS = [
  DeviceSyncField.BUSINESS_DATA,
  DeviceSyncField.USER_DATA,
  DeviceSyncField.TERMINALS,
  DeviceSyncField.BUSINESS_DEVICES,
  DeviceSyncField.PROVIDER_DETAILS,
];

type StartupFailure = { issue: ConnectivityIssue; message: string };
type ConnectivityCheckOptions = {
  /** Runtime transition scope; cold-start checks have no additional admission rule. */
  canCommit?: () => boolean;
  leaveOfflineMode?: boolean;
  refreshDeviceStatus?: boolean;
  deviceStatus?: ConnectionStatus;
  knownInternetReachable?: boolean;
  confirmUnavailable?: boolean;
};

function normalizeConnectionType(
  type: ConnectionStatus['connectionType']
): ConnectivityConnectionType {
  if (type === 'wifi' || type === 'cellular' || type === 'none') return type;
  return 'unknown';
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'PointCraft is temporarily unavailable';
}

const TRANSIENT_OFFLINE_ELIGIBILITY_REASONS: ReadonlySet<OfflineEligibilityReason> =
  new Set<OfflineEligibilityReason>(['device-session-unreadable', 'local-database-unavailable']);

function evaluateOfflineEligibilitySafe(): Promise<OfflineEligibility> {
  return evaluateOfflineEligibility().catch(() => ({
    eligible: false as const,
    reason: 'local-database-unavailable' as const,
    businessId: null,
    inventoryCount: 0,
  }));
}

/**
 * Reachability published alongside an unavailable issue. Steps after a failed
 * one are never probed: a device failure returns both later steps to "not
 * checked" (`null`). Internet and API failures publish their own measured
 * values before resolving, so those are kept as-is.
 */
function unavailableReachabilityPatch(
  issue: ConnectivityIssue,
  current: { internetReachable: boolean | null; apiReachable: boolean | null }
): { internetReachable: boolean | null; apiReachable: boolean | null } {
  if (issue === 'device') return { internetReachable: null, apiReachable: null };
  return {
    internetReachable: issue === 'internet' ? false : current.internetReachable,
    apiReachable: issue === 'api' ? false : current.apiReachable,
  };
}

async function probeAthStatus(
  signal: AbortSignal,
  timeoutMs: number,
  phase: ConnectivityHealthPhase
): Promise<boolean> {
  let complete: ReturnType<typeof beginConnectivityProbe> = () => undefined;
  let httpStatus: number | null = null;
  try {
    const baseUrl = useSystemStore.getState().baseAthUrl.replace(/\/$/, '');
    const url = `${baseUrl}/status?type=server`;
    complete = beginConnectivityProbe({
      probe: 'ath-status',
      url,
      timeoutMs,
      phase,
    });
    const response = await abortableProbe(
      trackedApiFetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: getHealthRequestHeaders(url),
        signal,
      }),
      signal
    );
    httpStatus = response.status;
    if (response.status !== 200) {
      complete('http-error', httpStatus);
      return false;
    }
    const payload = (await abortableProbe(response.json(), signal)) as {
      status?: unknown;
      result?: { status?: unknown };
    };
    const reachable = payload?.status === 'Online' || payload?.result?.status === 'Online';
    complete(reachable ? 'reachable' : 'invalid-payload', httpStatus);
    return reachable;
  } catch (error) {
    let outcome: 'network-error' | 'timeout' | 'superseded' | 'invalid-json' = 'network-error';
    if (signal.aborted) outcome = signal.reason === 'deadline' ? 'timeout' : 'superseded';
    else if (error instanceof SyntaxError) outcome = 'invalid-json';
    complete(outcome, httpStatus, error);
    return false;
  }
}

/**
 * The ATH /status host and the data-API host are separate deployments (dev
 * routes data traffic through a tunnel), so a healthy /status can coexist with
 * an intermediary answering 2xx HTML on the data host. This probes the exact
 * host every authenticated call uses and requires its health contract
 * ({"status":"ok"}); a non-JSON body makes response.json() throw, which
 * correctly reports the host unavailable.
 */
async function probeDataApiHealth(
  signal: AbortSignal,
  timeoutMs: number,
  phase: ConnectivityHealthPhase
): Promise<boolean> {
  let complete: ReturnType<typeof beginConnectivityProbe> = () => undefined;
  let httpStatus: number | null = null;
  try {
    const baseUrl = useSystemStore.getState().baseUrl.replace(/\/$/, '');
    const url = `${baseUrl}/api/health`;
    complete = beginConnectivityProbe({
      probe: 'data-api-health',
      url,
      timeoutMs,
      phase,
    });
    const response = await abortableProbe(
      trackedApiFetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: getHealthRequestHeaders(url),
        signal,
      }),
      signal
    );
    httpStatus = response.status;
    if (!response.ok) {
      complete('http-error', httpStatus);
      return false;
    }
    const payload = (await abortableProbe(response.json(), signal)) as { status?: unknown };
    const reachable = payload?.status === 'ok';
    complete(reachable ? 'reachable' : 'invalid-payload', httpStatus);
    return reachable;
  } catch (error) {
    let outcome: 'network-error' | 'timeout' | 'superseded' | 'invalid-json' = 'network-error';
    if (signal.aborted) outcome = signal.reason === 'deadline' ? 'timeout' : 'superseded';
    else if (error instanceof SyntaxError) outcome = 'invalid-json';
    complete(outcome, httpStatus, error);
    return false;
  }
}

async function probeFirstPartyApi(
  timeoutMs: number,
  phase: ConnectivityHealthPhase,
  signal: AbortSignal
): Promise<boolean> {
  // QA-only Debug Admin override: report the API as unreachable without
  // touching the real endpoints, so the full api-outage flow can be exercised.
  if (isFirstPartyApiOutageSimulated()) return false;
  const controller = new AbortController();
  const abort = () => controller.abort('superseded');
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
  const timeoutId = window.setTimeout(() => controller.abort('deadline'), timeoutMs);
  try {
    // PointCraft is reachable only when BOTH first-party hosts answer their
    // contracts; a healthy status host cannot prove data calls will succeed.
    const [athOnline, dataApiHealthy] = await Promise.all([
      probeAthStatus(controller.signal, timeoutMs, phase),
      probeDataApiHealth(controller.signal, timeoutMs, phase),
    ]);
    return athOnline && dataApiHealthy;
  } finally {
    window.clearTimeout(timeoutId);
    signal.removeEventListener('abort', abort);
  }
}

async function probeInternetAccess(signal: AbortSignal): Promise<boolean> {
  const complete = beginConnectivityProbe({
    probe: 'internet',
    url: INTERNET_ACCESS_CHECK_URL,
    timeoutMs: INTERNET_REQUEST_TIMEOUT_MS,
  });
  const controller = new AbortController();
  const abort = () => controller.abort('superseded');
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
  const timeoutId = window.setTimeout(
    () => controller.abort('deadline'),
    INTERNET_REQUEST_TIMEOUT_MS
  );
  try {
    await abortableProbe(requestInternetAccessProbe(controller.signal), controller.signal);
    // The no-cors response is opaque; no HTTP status or body can be inspected.
    complete('reachable');
    return true;
  } catch (error) {
    let outcome: 'network-error' | 'timeout' | 'superseded' = 'network-error';
    if (controller.signal.aborted)
      outcome = controller.signal.reason === 'deadline' ? 'timeout' : 'superseded';
    complete(outcome, null, error);
    return false;
  } finally {
    window.clearTimeout(timeoutId);
    signal.removeEventListener('abort', abort);
  }
}

export class ConnectivityCoordinator {
  private referenceCount = 0;

  private lifecycleVersion = 0;

  private scheduledCheckTimer: number | null = null;

  private transientEligibilityTimer: number | null = null;

  private transientEligibilityAttempts = 0;

  private sessionRepairTimer: number | null = null;

  private sessionRepairRetryCount = 0;

  private failureGraceTimer: number | null = null;

  private failureResolutionVersion = 0;

  private offlineEntryVersion = 0;

  private checkVersion = 0;

  private checkController = new AbortController();

  private suspended = false;

  /** Set once by `suspendForShutdown`: the app is closing and never checks again. */
  private shutDown = false;

  private suspectedApiFailure = false;

  private activeCheck: Promise<boolean> | null = null;

  private activeInternetHeartbeat: Promise<boolean> | null = null;

  private lastFirstPartyCheckAt: number | null = null;

  private removeNetworkListener: (() => Promise<void>) | null = null;

  private removeAppStateListener: (() => Promise<void>) | null = null;

  private removeAuthStoreListener: (() => void) | null = null;

  private removeDeviceSyncListener: (() => void) | null = null;

  private runtimeRecoveryReady: (() => boolean) | null = null;

  registerRuntimeRecoveryGuard = (ready: () => boolean): (() => void) => {
    this.runtimeRecoveryReady = ready;
    return () => {
      if (this.runtimeRecoveryReady === ready) this.runtimeRecoveryReady = null;
    };
  };

  private recoveryTasks = new Map<string, StartupRecoveryTask>();

  private startupFailures = new Map<string, StartupFailure>();

  private needsAuthenticatedRecovery = false;

  private readonly handleBrowserOnline = () => this.handleDeviceHint(true, 'unknown');

  private readonly handleBrowserOffline = () => this.handleDeviceHint(false, 'none');

  private readonly handleFirstPartyTransportFailure = () => {
    if (useConnectivityStore.getState().runtimeState !== 'ONLINE') return;
    this.checkNow({ refreshDeviceStatus: true }).catch(() => undefined);
  };

  private readonly handleVisibilityChange = () => {
    this.handleAppVisibility(document.visibilityState !== 'hidden');
  };

  private handleAppVisibility = (isActive: boolean): void => {
    this.suspended = !isActive;
    if (!isActive) {
      this.invalidateChecks();
      this.invalidatePendingUnavailableState();
      this.clearScheduledCheck();
      return;
    }
    // Background elapsed time is not outage evidence. Start with fresh health.
    this.checkNow({ refreshDeviceStatus: true }).catch(() => undefined);
  };

  start = async (): Promise<void> => {
    if (this.shutDown) return;
    this.referenceCount += 1;
    if (this.referenceCount > 1) return;
    this.lifecycleVersion += 1;
    this.suspended = false;
    const { lifecycleVersion } = this;

    window.addEventListener('online', this.handleBrowserOnline);
    window.addEventListener('offline', this.handleBrowserOffline);
    window.addEventListener(
      FIRST_PARTY_TRANSPORT_FAILURE_EVENT,
      this.handleFirstPartyTransportFailure
    );
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.removeAuthStoreListener = useAuthStore.subscribe((state, previous) => {
      if (!state.authenticated && previous.authenticated) {
        this.clearSessionRepairRetry();
        resetDeviceSessionRepairState();
        return;
      }
      if (
        state.authenticated &&
        !previous.authenticated &&
        useConnectivityStore.getState().runtimeState === 'ONLINE'
      ) {
        this.attemptDeviceSessionRepair();
      }
    });
    this.removeDeviceSyncListener = useDeviceSyncStore.subscribe((state, previous) => {
      const hydrationCompleted =
        (state.status === 'hydrated' && previous.status !== 'hydrated') ||
        (state.status === 'ready' &&
          (previous.status !== 'ready' || state.cachedAt !== previous.cachedAt));
      if (
        hydrationCompleted &&
        state.businessData?.id &&
        useConnectivityStore.getState().runtimeState === 'ONLINE'
      ) {
        this.attemptDeviceSessionRepair();
      }
    });

    try {
      const appStateListener = await App.addListener('appStateChange', ({ isActive }) => {
        this.handleAppVisibility(isActive);
      });
      if (lifecycleVersion !== this.lifecycleVersion || this.referenceCount === 0) {
        await appStateListener.remove();
        return;
      }
      this.removeAppStateListener = () => appStateListener.remove();
    } catch {
      // Browser visibility and network events remain authoritative fallbacks.
    }

    let initialStatus: ConnectionStatus;
    const initialDeviceVersion = this.checkVersion;
    try {
      const listener = await Network.addListener('networkStatusChange', (status) => {
        this.handleDeviceHint(status.connected, normalizeConnectionType(status.connectionType));
      });
      if (lifecycleVersion !== this.lifecycleVersion || this.referenceCount === 0) {
        await listener.remove();
        return;
      }
      this.removeNetworkListener = () => listener.remove();
      const status = await Network.getStatus();
      if (
        lifecycleVersion !== this.lifecycleVersion ||
        this.referenceCount === 0 ||
        initialDeviceVersion !== this.checkVersion
      )
        return;
      initialStatus = status;
      useConnectivityStore.getState().update({
        deviceConnected: status.connected,
        connectionType: normalizeConnectionType(status.connectionType),
      });
    } catch {
      if (
        lifecycleVersion !== this.lifecycleVersion ||
        this.referenceCount === 0 ||
        initialDeviceVersion !== this.checkVersion
      )
        return;
      initialStatus = {
        connected: navigator.onLine,
        connectionType: navigator.onLine ? 'unknown' : 'none',
      };
      useConnectivityStore.getState().update({
        deviceConnected: navigator.onLine,
        connectionType: navigator.onLine ? 'unknown' : 'none',
      });
    }

    if (lifecycleVersion !== this.lifecycleVersion || this.referenceCount === 0) return;
    await this.checkNow({ deviceStatus: initialStatus });
  };

  stop = (): void => {
    this.referenceCount = Math.max(0, this.referenceCount - 1);
    if (this.referenceCount > 0) return;
    this.lifecycleVersion += 1;
    this.invalidateChecks();
    window.removeEventListener('online', this.handleBrowserOnline);
    window.removeEventListener('offline', this.handleBrowserOffline);
    window.removeEventListener(
      FIRST_PARTY_TRANSPORT_FAILURE_EVENT,
      this.handleFirstPartyTransportFailure
    );
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.clearScheduledCheck();
    this.clearTransientEligibilityRetry();
    this.clearSessionRepairRetry();
    this.invalidatePendingUnavailableState();
    this.removeNetworkListener?.().catch(() => undefined);
    this.removeNetworkListener = null;
    this.removeAppStateListener?.().catch(() => undefined);
    this.removeAppStateListener = null;
    this.removeAuthStoreListener?.();
    this.removeAuthStoreListener = null;
    this.removeDeviceSyncListener?.();
    this.removeDeviceSyncListener = null;
  };

  /**
   * The app is closing (`src/features/app-exit`): stop every check, listener
   * and retry now, whatever the reference count, so no recovery page or
   * offline flow starts while the register shuts down. Final for this page.
   */
  suspendForShutdown = (): void => {
    if (this.shutDown) return;
    this.shutDown = true;
    this.referenceCount = Math.min(this.referenceCount, 1);
    if (this.referenceCount === 1) this.stop();
  };

  registerRecoveryTask = (key: string, task: StartupRecoveryTask): (() => void) => {
    this.recoveryTasks.set(key, task);
    return () => this.recoveryTasks.delete(key);
  };

  reportStartupFailure = (
    key: string,
    error: unknown,
    issue: ConnectivityIssue = 'startup'
  ): void => {
    this.invalidateChecks();
    this.invalidatePendingUnavailableState();
    const message = errorMessage(error);
    this.startupFailures.set(key, { issue, message });
    this.needsAuthenticatedRecovery = true;
    this.resolveUnavailableState(issue, message).catch(() => undefined);
  };

  clearStartupFailure = (key: string): void => {
    this.startupFailures.delete(key);
  };

  enterOfflineMode = async (options?: { canCommit?: () => boolean }): Promise<boolean> => {
    if (options?.canCommit && !options.canCommit()) return false;
    if (getCurrentOfflineShellBlockReason() === 'identity-unverified') {
      // The post-login identity check fails closed on transient local reads;
      // retry with a fresh device-session load before refusing manual entry.
      await retryOfflineIdentityVerification().catch(() => false);
    }
    if (!getCurrentOfflineShellAvailable()) return false;

    // Every manual entry re-evaluates eligibility: the stored snapshot can be
    // the synchronous EMPTY reset of a superseded outage resolution, or simply
    // stale. Awaiting BEFORE flipping runtimeState keeps the entry atomic, so
    // OFFLINE_MODE never appears with a wrong verdict and the connectivity
    // boundary cannot momentarily evict a retained payment page.
    const offlineEligibility: OfflineEligibility = await evaluateOfflineEligibilitySafe();
    if (!getCurrentOfflineShellAvailable() || (options?.canCommit && !options.canCommit()))
      return false;

    // A health check that began before this deliberate mode change may still
    // resolve. It must not undo the newly admitted offline session.
    this.offlineEntryVersion += 1;
    this.invalidateChecks();
    this.needsAuthenticatedRecovery = true;
    this.invalidatePendingUnavailableState();
    this.clearScheduledCheck();
    this.clearTransientEligibilityRetry();
    this.clearSessionRepairRetry();
    useConnectivityStore.getState().update({
      runtimeState: 'OFFLINE_MODE',
      offlineEligibility,
    });
    this.maybeScheduleTransientEligibilityRetry(offlineEligibility);
    this.scheduleNextCheck();
    return true;
  };

  showRecoveryPage = (): boolean => {
    const { runtimeState } = useConnectivityStore.getState();
    if (runtimeState !== 'OFFLINE_MODE') return false;

    this.clearScheduledCheck();
    this.clearTransientEligibilityRetry();
    this.clearSessionRepairRetry();
    useConnectivityStore.getState().update({
      runtimeState: getCurrentOfflineShellAvailable() ? 'OFFLINE_AVAILABLE' : 'CONNECTION_REQUIRED',
    });
    this.scheduleNextCheck();
    return true;
  };

  leaveOfflineMode = async (options?: { canCommit?: () => boolean }): Promise<boolean> =>
    this.checkNow({ ...options, leaveOfflineMode: true, refreshDeviceStatus: true });

  retryNow = (options?: { canCommit?: () => boolean }): Promise<boolean> =>
    this.checkNow({ ...options, leaveOfflineMode: true, refreshDeviceStatus: true });

  /**
   * Re-evaluate offline transaction eligibility on demand and publish the
   * verdict, so operator-facing fresh checks (Debug Admin, the go-offline
   * dialog) heal the cached snapshot the payment page renders from. A newer
   * outage resolution wins: its version guard drops this publish. Safe in any
   * runtime state — while ONLINE nothing gates on the snapshot, so the publish
   * is inert until the next offline transition.
   */
  refreshOfflineEligibility = async (): Promise<OfflineEligibility> => {
    const resolutionVersion = this.failureResolutionVersion;
    const offlineEligibility = await evaluateOfflineEligibilitySafe();
    if (resolutionVersion === this.failureResolutionVersion) {
      useConnectivityStore.getState().update({ offlineEligibility });
      this.maybeScheduleTransientEligibilityRetry(offlineEligibility);
    }
    return offlineEligibility;
  };

  private handleDeviceHint = (
    connected: boolean,
    connectionType: ConnectivityConnectionType
  ): void => {
    const previous = useConnectivityStore.getState();
    if (previous.deviceConnected !== connected || previous.connectionType !== connectionType) {
      this.invalidateChecks();
      this.invalidatePendingUnavailableState();
    }
    useConnectivityStore.getState().update({
      deviceConnected: connected,
      connectionType,
      // Later steps are only verified after the device step passes, so a
      // disconnect returns them to "not checked" rather than "checked and failed".
      ...(!connected ? { internetReachable: null, apiReachable: null } : {}),
    });
    if (!connected) {
      this.clearSessionRepairRetry();
      this.needsAuthenticatedRecovery = true;
      this.resolveUnavailableState('device', 'This device is not connected to a network').catch(
        () => undefined
      );
      return;
    }
    this.checkNow({
      deviceStatus: {
        connected,
        connectionType,
      },
    }).catch(() => undefined);
  };

  private checkNow = (options: ConnectivityCheckOptions = {}): Promise<boolean> => {
    if (options.canCommit?.() === false || this.suspended || document.visibilityState === 'hidden')
      return Promise.resolve(false);
    if (this.activeCheck) {
      const { activeCheck } = this;
      const { runtimeState } = useConnectivityStore.getState();
      if (options.leaveOfflineMode && runtimeState === 'OFFLINE_MODE') {
        return activeCheck.then((result) => {
          const latestState = useConnectivityStore.getState();
          if (options.canCommit?.() === false) return false;
          if (latestState.runtimeState !== 'OFFLINE_MODE') return result;
          return this.checkNow(options);
        });
      }
      return options.canCommit
        ? activeCheck.then((result) => options.canCommit?.() !== false && result)
        : activeCheck;
    }

    // Hints and manual retries join the pending confirmation without restarting
    // its two-second grace or creating another initial probe.
    if (this.suspectedApiFailure && !options.confirmUnavailable) return Promise.resolve(false);

    this.invalidatePendingUnavailableState();
    this.clearScheduledCheck();
    this.checkVersion += 1;
    const version = this.checkVersion;
    const check = this.performCheck(options)
      .catch(() => false)
      .finally(() => {
        if (version !== this.checkVersion) return;
        this.activeCheck = null;
        useConnectivityStore.getState().update({ checkStage: 'idle' });
        this.scheduleNextCheck();
      });
    this.activeCheck = check;
    return this.activeCheck;
  };

  private probeTrackedFirstPartyApi = async (
    timeoutMs: number,
    phase: ConnectivityHealthPhase,
    signal: AbortSignal
  ): Promise<boolean> => {
    const reachable = await probeFirstPartyApi(timeoutMs, phase, signal);
    if (!signal.aborted) this.lastFirstPartyCheckAt = Date.now();
    return reachable;
  };

  private runOnlineInternetHeartbeat = (): Promise<boolean> => {
    if (this.activeInternetHeartbeat) return this.activeInternetHeartbeat;

    const { lifecycleVersion, checkVersion } = this;
    const heartbeat = (async () => {
      const internetReachable = await probeInternetAccess(this.checkController.signal);
      if (
        lifecycleVersion !== this.lifecycleVersion ||
        checkVersion !== this.checkVersion ||
        this.referenceCount === 0
      )
        return false;

      const { runtimeState } = useConnectivityStore.getState();
      if (runtimeState !== 'ONLINE') return false;

      if (internetReachable) {
        useConnectivityStore.getState().update({ internetReachable: true });
        return true;
      }

      return this.checkNow({
        refreshDeviceStatus: true,
        knownInternetReachable: false,
      });
    })().finally(() => {
      if (this.activeInternetHeartbeat !== heartbeat) return;
      this.activeInternetHeartbeat = null;
      this.scheduleNextCheck();
    });
    this.activeInternetHeartbeat = heartbeat;
    return this.activeInternetHeartbeat;
  };

  private async performCheck(options: ConnectivityCheckOptions): Promise<boolean> {
    const { offlineEntryVersion, checkVersion, lifecycleVersion } = this;
    const { signal } = this.checkController;
    const current = () =>
      offlineEntryVersion === this.offlineEntryVersion &&
      checkVersion === this.checkVersion &&
      lifecycleVersion === this.lifecycleVersion &&
      !signal.aborted &&
      options.canCommit?.() !== false;
    if (!current()) return false;
    const previous = useConnectivityStore.getState();
    if (previous.runtimeState === 'CHECKING') this.needsAuthenticatedRecovery = true;
    let phase: ConnectivityHealthPhase = 'recovery';
    if (previous.runtimeState === 'ONLINE' || previous.runtimeState === 'CHECKING')
      phase = 'initial';
    if (options.confirmUnavailable) phase = 'confirmation';
    const timeoutMs =
      phase === 'initial'
        ? CONNECTIVITY_FIRST_PARTY_REQUEST_TIMEOUT_MS
        : CONNECTIVITY_CONFIRMATION_TIMEOUT_MS;
    const probeApi = () => this.probeTrackedFirstPartyApi(timeoutMs, phase, signal);
    const resolveUnavailable = (issue: ConnectivityIssue, message: string): Promise<void> =>
      current()
        ? this.resolveUnavailableState(issue, message, options.confirmUnavailable === true)
        : Promise.resolve();
    const validatingOfflineExit =
      previous.runtimeState === 'OFFLINE_MODE' && options.leaveOfflineMode === true;
    const preserveOfflineMode =
      previous.runtimeState === 'OFFLINE_MODE' && !options.leaveOfflineMode;
    if (!preserveOfflineMode && !validatingOfflineExit) {
      const shouldEnterVisibleRecovery =
        previous.runtimeState === 'OFFLINE_AVAILABLE' ||
        previous.runtimeState === 'CONNECTION_REQUIRED' ||
        previous.runtimeState === 'RECOVERING' ||
        previous.runtimeState === 'OFFLINE_MODE';

      if (shouldEnterVisibleRecovery) {
        useConnectivityStore.getState().update({
          runtimeState: 'RECOVERING',
          message: null,
        });
      } else if (previous.runtimeState === 'CHECKING') {
        useConnectivityStore.getState().update({ message: null });
      }
    }

    // Keep recovery row progress in order, but only a device disconnect may
    // skip PointCraft. Google remains diagnostic in every runtime state.
    const stagedRecovery =
      previous.runtimeState !== 'ONLINE' && previous.runtimeState !== 'CHECKING';
    if (stagedRecovery) {
      useConnectivityStore.getState().update({
        checkStage: 'device',
        internetReachable: null,
        apiReachable: null,
      });
    }

    let networkStatus = options.deviceStatus;
    if (options.refreshDeviceStatus) {
      try {
        networkStatus = await Network.getStatus();
      } catch {
        networkStatus = {
          connected: navigator.onLine,
          connectionType: navigator.onLine ? 'unknown' : 'none',
        };
      }
    }
    if (!networkStatus) {
      networkStatus = {
        connected: previous.deviceConnected,
        connectionType: previous.connectionType,
      };
    }

    if (!current()) return false;
    const deviceConnected = networkStatus.connected;
    useConnectivityStore.getState().update({
      deviceConnected,
      connectionType: normalizeConnectionType(networkStatus.connectionType),
      ...(!deviceConnected ? { internetReachable: null, apiReachable: null } : {}),
    });
    if (!deviceConnected) {
      this.needsAuthenticatedRecovery = true;
      await resolveUnavailable('device', 'This device is not connected to a network');
      return false;
    }

    let internetReachable: boolean | null;
    let apiReachable: boolean;
    if (stagedRecovery) {
      useConnectivityStore.getState().update({ checkStage: 'internet' });
      internetReachable = await probeInternetAccess(signal);
      if (!current()) return false;
      // A disconnect event can arrive while the probe is in flight; keep the
      // newer device signal instead of continuing to the next step.
      if (!useConnectivityStore.getState().deviceConnected) {
        this.needsAuthenticatedRecovery = true;
        await resolveUnavailable('device', 'This device is not connected to a network');
        return false;
      }
      useConnectivityStore.getState().update({ internetReachable, checkStage: 'api' });
      apiReachable = await probeApi();
    } else if (options.knownInternetReachable !== undefined) {
      internetReachable = options.knownInternetReachable;
      apiReachable = await probeApi();
    } else {
      [internetReachable, apiReachable] = await Promise.all([
        probeInternetAccess(signal),
        probeApi(),
      ]);
    }

    if (!current()) return false;
    // A native/browser disconnect event can arrive while either request is in flight.
    // Do not let a late response overwrite the newer device signal.
    if (!useConnectivityStore.getState().deviceConnected) {
      this.needsAuthenticatedRecovery = true;
      await resolveUnavailable('device', 'This device is not connected to a network');
      return false;
    }

    if (!apiReachable) {
      const suspect = phase === 'initial';
      reportConnectivityHealthDecision({
        phase,
        timeoutMs,
        outage: suspect ? 'suspected' : 'confirmed',
        internetReachable,
      });
      if (suspect) {
        this.suspectedApiFailure = true;
        this.clearScheduledCheck();
        const resolutionVersion = this.failureResolutionVersion;
        this.failureGraceTimer = window.setTimeout(() => {
          if (resolutionVersion !== this.failureResolutionVersion) return;
          this.failureGraceTimer = null;
          if (!current()) {
            this.invalidatePendingUnavailableState();
            this.scheduleNextCheck();
            return;
          }
          this.checkNow({
            ...options,
            refreshDeviceStatus: true,
            confirmUnavailable: true,
            knownInternetReachable: undefined,
          }).catch(() => undefined);
        }, CONNECTIVITY_ISSUE_GRACE_MS);
        return false;
      }
      this.needsAuthenticatedRecovery = true;
      useConnectivityStore.getState().update({
        internetReachable,
        apiReachable: false,
      });
      await resolveUnavailable(
        internetReachable ? 'api' : 'internet',
        internetReachable
          ? 'PointCraft services could not be reached'
          : 'This network could not reach the internet'
      );
      return false;
    }

    reportConnectivityHealthDecision({ phase, timeoutMs, outage: 'cleared', internetReachable });

    // Publish the verified reachability before recovery work so a startup-task
    // failure keeps the confirmed step results instead of "not checked".
    useConnectivityStore.getState().update({
      internetReachable,
      apiReachable: true,
      checkStage: 'idle',
    });

    // Offline heartbeats only establish reachability. The runtime transition
    // controller admits authenticated recovery after its stability/payment hold.
    if (preserveOfflineMode && useConnectivityStore.getState().runtimeState === 'OFFLINE_MODE')
      return true;
    // Quiet confirmation belongs only to an established session. It must not
    // bootstrap auth, touch terminal assignments, or run registered recovery.
    const authStatus = useAuthStore.getState().authRestorationStatus;
    const reachabilityOnly =
      previous.runtimeState === 'ONLINE' &&
      !this.needsAuthenticatedRecovery &&
      this.startupFailures.size === 0 &&
      authStatus !== 'RESTORING' &&
      authStatus !== 'RECOVERY_REQUIRED';
    if (reachabilityOnly) {
      this.invalidatePendingUnavailableState();
      return true;
    }
    const recoveryAdmitted = () =>
      current() &&
      (previous.runtimeState === 'CHECKING' || this.runtimeRecoveryReady?.() !== false);
    if (!recoveryAdmitted()) return false;

    try {
      const authStatusBeforeRecovery = useAuthStore.getState().authRestorationStatus;
      const shouldRecoverAuth =
        SITE_ID === SiteIdEnum.PointOfSale &&
        (this.needsAuthenticatedRecovery ||
          authStatusBeforeRecovery === 'RESTORING' ||
          authStatusBeforeRecovery === 'RECOVERY_REQUIRED');
      if (shouldRecoverAuth) {
        try {
          const restorationStatus = await recoverAuthSession();
          if (!recoveryAdmitted()) return false;
          if (restorationStatus === 'AUTHENTICATED' || restorationStatus === 'SIGNED_OUT') {
            this.startupFailures.delete('auth-restore');
          }
        } catch (error) {
          if (!current()) return false;
          this.startupFailures.set('auth-restore', {
            issue: 'startup',
            message:
              'Your saved sign-in could not be restored yet. Keep this device online and retry.',
          });
          throw error;
        }
      }

      const authState = useAuthStore.getState();
      const shouldRunAuthenticatedPosRecovery =
        SITE_ID === SiteIdEnum.PointOfSale &&
        authState.authRestorationStatus === 'AUTHENTICATED' &&
        authState.authenticated &&
        Boolean(authState.user);
      if (shouldRunAuthenticatedPosRecovery) {
        if (this.needsAuthenticatedRecovery) {
          const applicationContext = await recoverMissingApplicationContext();
          if (!recoveryAdmitted()) return false;
          if (applicationContext === 'pending') {
            throw new Error('The device context is still being restored');
          }
        }
        // Terminal assignment is an authenticated ordering barrier. It must settle before the
        // broad Device Sync refresh and before payment-specific recovery runners observe ONLINE.
        if (!recoveryAdmitted()) return false;
        await drainTerminalAssignmentsBeforeOnline();
      }
      if (this.needsAuthenticatedRecovery && shouldRunAuthenticatedPosRecovery) {
        if (!recoveryAdmitted()) return false;
        await syncDeviceSyncV4Fields(REQUIRED_RECOVERY_FIELDS);
      }

      // Recovery tasks are deliberately serialized to avoid overlapping startup writes.
      await Array.from(this.recoveryTasks.entries()).reduce(async (previousTask, [key, task]) => {
        await previousTask;
        if (!recoveryAdmitted()) return;
        await task();
        if (!recoveryAdmitted()) return;
        this.startupFailures.delete(key);
      }, Promise.resolve());

      if (!recoveryAdmitted()) return false;
      this.needsAuthenticatedRecovery = false;
      if (!recoveryAdmitted()) return false;
      this.invalidatePendingUnavailableState();
      const retainOfflineMode =
        preserveOfflineMode && useConnectivityStore.getState().runtimeState === 'OFFLINE_MODE';
      useConnectivityStore.getState().update({
        runtimeState: retainOfflineMode ? 'OFFLINE_MODE' : 'ONLINE',
        issue: 'none',
        message: retainOfflineMode ? previous.message : null,
        internetReachable,
        apiReachable: true,
      });
      if (!retainOfflineMode) {
        // A register that lost its offline device session self-heals while
        // online. Device Sync hydration can happen after this transition, and
        // its store subscription above invokes the same bounded repair path.
        this.attemptDeviceSessionRepair();
      }
      return true;
    } catch (error) {
      const failure = Array.from(this.startupFailures.values())[0];
      await resolveUnavailable(
        failure?.issue ?? 'startup',
        failure?.message ?? errorMessage(error)
      );
      return false;
    }
  }

  private resolveUnavailableState(
    issue: ConnectivityIssue,
    message: string,
    confirmedUnavailable = false
  ): Promise<void> {
    this.clearScheduledCheck();
    this.clearSessionRepairRetry();
    const stateAtFailure = useConnectivityStore.getState();
    const preserveOfflineMode = stateAtFailure.runtimeState === 'OFFLINE_MODE';
    const shouldApplyGrace =
      !confirmedUnavailable &&
      !preserveOfflineMode &&
      (stateAtFailure.runtimeState === 'CHECKING' ||
        stateAtFailure.runtimeState === 'ONLINE' ||
        stateAtFailure.runtimeState === 'CONNECTION_ISSUE');

    if (
      shouldApplyGrace &&
      this.failureGraceTimer !== null &&
      stateAtFailure.issue === issue &&
      stateAtFailure.message === message
    ) {
      return Promise.resolve();
    }

    this.failureResolutionVersion += 1;
    const resolutionVersion = this.failureResolutionVersion;
    if (this.failureGraceTimer !== null) window.clearTimeout(this.failureGraceTimer);
    this.failureGraceTimer = null;

    useConnectivityStore.getState().update({
      ...(shouldApplyGrace ? { runtimeState: 'CONNECTION_ISSUE' as const } : {}),
      issue,
      message,
      ...unavailableReachabilityPatch(issue, stateAtFailure),
    });

    const offlineEligibilityPromise: Promise<OfflineEligibility> = evaluateOfflineEligibilitySafe();

    const publishOfflineEligibility = async (): Promise<void> => {
      const offlineEligibility = await offlineEligibilityPromise;
      if (resolutionVersion !== this.failureResolutionVersion) return;

      useConnectivityStore.getState().update({ offlineEligibility });
      this.maybeScheduleTransientEligibilityRetry(offlineEligibility);
    };

    const finalizeUnavailableState = (): void => {
      if (resolutionVersion !== this.failureResolutionVersion) return;

      const current = useConnectivityStore.getState();
      const keepOfflineMode = current.runtimeState === 'OFFLINE_MODE';
      let { runtimeState } = current;
      if (!keepOfflineMode) {
        runtimeState = getCurrentOfflineShellAvailable()
          ? 'OFFLINE_AVAILABLE'
          : 'CONNECTION_REQUIRED';
      }

      useConnectivityStore.getState().update({
        runtimeState,
        issue,
        message,
        ...unavailableReachabilityPatch(issue, current),
        ...(!keepOfflineMode ? { offlineEligibility: EMPTY_OFFLINE_ELIGIBILITY } : {}),
      });
      publishOfflineEligibility().catch(() => undefined);
      this.scheduleNextCheck();
    };

    if (!shouldApplyGrace) {
      finalizeUnavailableState();
      return Promise.resolve();
    }

    this.failureGraceTimer = window.setTimeout(() => {
      if (resolutionVersion !== this.failureResolutionVersion) return;
      this.failureGraceTimer = null;
      // Do not perform background network work while hidden. The existing
      // visibility/app-state handlers run the confirmation immediately after
      // the register returns to the foreground.
      if (document.visibilityState === 'hidden') return;
      this.checkNow({
        refreshDeviceStatus: true,
        confirmUnavailable: true,
      }).catch(() => undefined);
    }, CONNECTIVITY_ISSUE_GRACE_MS);
    return Promise.resolve();
  }

  private scheduleNextCheck(): void {
    if (
      this.referenceCount === 0 ||
      this.scheduledCheckTimer ||
      this.activeCheck ||
      this.activeInternetHeartbeat ||
      this.failureGraceTimer !== null ||
      this.suspectedApiFailure ||
      this.suspended
    ) {
      return;
    }

    const { runtimeState } = useConnectivityStore.getState();
    if (document.visibilityState === 'hidden') return;
    const firstPartyCheckDelay =
      this.lastFirstPartyCheckAt === null
        ? 0
        : Math.max(
            0,
            CONNECTIVITY_ONLINE_CHECK_INTERVAL_MS - (Date.now() - this.lastFirstPartyCheckAt)
          );
    let delay = CONNECTIVITY_RETRY_INTERVAL_MS;
    if (runtimeState === 'ONLINE') {
      delay = Math.min(CONNECTIVITY_INTERNET_CHECK_INTERVAL_MS, firstPartyCheckDelay);
    } else if (runtimeState === 'OFFLINE_MODE') {
      delay = CONNECTIVITY_OFFLINE_MODE_RECHECK_INTERVAL_MS;
    }
    this.scheduledCheckTimer = window.setTimeout(() => {
      this.scheduledCheckTimer = null;
      const currentRuntimeState = useConnectivityStore.getState().runtimeState;
      if (currentRuntimeState !== 'ONLINE') {
        // The periodic recovery recheck re-verifies the full chain from the
        // device step onward, in order.
        this.checkNow({ refreshDeviceStatus: true }).catch(() => undefined);
        return;
      }

      const firstPartyCheckDue =
        this.lastFirstPartyCheckAt === null ||
        Date.now() - this.lastFirstPartyCheckAt >= CONNECTIVITY_ONLINE_CHECK_INTERVAL_MS;
      if (firstPartyCheckDue) {
        this.checkNow().catch(() => undefined);
        return;
      }

      this.runOnlineInternetHeartbeat().catch(() => undefined);
    }, delay);
  }

  private clearScheduledCheck(): void {
    if (this.scheduledCheckTimer) window.clearTimeout(this.scheduledCheckTimer);
    this.scheduledCheckTimer = null;
  }

  /**
   * Bounded 5s re-evaluation after a transient eligibility verdict while in
   * OFFLINE_MODE, so one bad secure-storage/local-DB read cannot leave Cash
   * and Card latched off for the full 60s recovery interval. A non-transient
   * verdict resets the attempt budget; leaving OFFLINE_MODE cancels the loop.
   */
  private maybeScheduleTransientEligibilityRetry(eligibility: OfflineEligibility): void {
    if (eligibility.eligible || !TRANSIENT_OFFLINE_ELIGIBILITY_REASONS.has(eligibility.reason)) {
      this.clearTransientEligibilityRetry();
      return;
    }
    if (
      this.referenceCount === 0 ||
      this.transientEligibilityTimer !== null ||
      this.transientEligibilityAttempts >= CONNECTIVITY_TRANSIENT_ELIGIBILITY_MAX_ATTEMPTS ||
      useConnectivityStore.getState().runtimeState !== 'OFFLINE_MODE'
    ) {
      return;
    }
    this.transientEligibilityAttempts += 1;
    this.transientEligibilityTimer = window.setTimeout(() => {
      this.transientEligibilityTimer = null;
      if (useConnectivityStore.getState().runtimeState !== 'OFFLINE_MODE') return;
      // Hidden tabs stay quiet (the scheduleNextCheck invariant); the
      // visibility-resume checkNow re-evaluates eligibility on return.
      if (document.visibilityState === 'hidden') return;
      this.refreshOfflineEligibility().catch(() => undefined);
    }, CONNECTIVITY_TRANSIENT_ELIGIBILITY_RETRY_MS);
  }

  private clearTransientEligibilityRetry(): void {
    if (this.transientEligibilityTimer !== null) {
      window.clearTimeout(this.transientEligibilityTimer);
    }
    this.transientEligibilityTimer = null;
    this.transientEligibilityAttempts = 0;
  }

  private attemptDeviceSessionRepair(): void {
    if (
      this.referenceCount === 0 ||
      useConnectivityStore.getState().runtimeState !== 'ONLINE' ||
      !useAuthStore.getState().authenticated
    ) {
      this.clearSessionRepairRetry();
      return;
    }

    maybeRepairDeviceSession()
      .then((outcome) => {
        if (this.referenceCount === 0) return;
        if (outcome === 'repaired') {
          this.clearSessionRepairRetry();
          this.refreshOfflineEligibility().catch(() => undefined);
          return;
        }
        this.maybeScheduleSessionRepairRetry(outcome);
      })
      .catch(() => {
        this.maybeScheduleSessionRepairRetry('transient-failure');
      });
  }

  private maybeScheduleSessionRepairRetry(outcome: DeviceSessionRepairOutcome): void {
    const retryable =
      outcome === 'transient-failure' ||
      outcome === 'throttled' ||
      outcome === 'storage-unreadable';
    if (!retryable) {
      this.clearSessionRepairRetry();
      return;
    }
    if (
      this.referenceCount === 0 ||
      this.sessionRepairTimer !== null ||
      this.sessionRepairRetryCount >= CONNECTIVITY_SESSION_REPAIR_MAX_RETRIES ||
      useConnectivityStore.getState().runtimeState !== 'ONLINE' ||
      !useAuthStore.getState().authenticated
    ) {
      return;
    }

    this.sessionRepairRetryCount += 1;
    const delay =
      outcome === 'storage-unreadable'
        ? CONNECTIVITY_TRANSIENT_ELIGIBILITY_RETRY_MS
        : Math.max(
            CONNECTIVITY_TRANSIENT_ELIGIBILITY_RETRY_MS,
            getDeviceSessionRepairRetryDelayMs()
          );
    this.sessionRepairTimer = window.setTimeout(() => {
      this.sessionRepairTimer = null;
      if (
        useConnectivityStore.getState().runtimeState !== 'ONLINE' ||
        !useAuthStore.getState().authenticated
      ) {
        this.clearSessionRepairRetry();
        return;
      }
      this.attemptDeviceSessionRepair();
    }, delay);
  }

  private clearSessionRepairRetry(): void {
    if (this.sessionRepairTimer !== null) window.clearTimeout(this.sessionRepairTimer);
    this.sessionRepairTimer = null;
    this.sessionRepairRetryCount = 0;
  }

  private invalidatePendingUnavailableState(): void {
    this.failureResolutionVersion += 1;
    if (this.failureGraceTimer !== null) window.clearTimeout(this.failureGraceTimer);
    this.failureGraceTimer = null;
    this.suspectedApiFailure = false;
  }

  private invalidateChecks(): void {
    this.checkVersion += 1;
    this.checkController.abort();
    this.checkController = new AbortController();
    this.activeCheck = null;
    this.activeInternetHeartbeat = null;
  }
}

export const connectivityCoordinator = new ConnectivityCoordinator();
